/* eslint-disable ember/no-mixins, no-unused-vars */
import { tracked } from '@glimmer/tracking';
import Controller from '@ember/controller';
import { action, computed } from '@ember/object';
import { filterBy, alias } from '@ember/object/computed';
import { service } from '@ember/service';
import config from 'linced-app/config/environment';
import GroupType from '@linc-technologies/linced-common/enums/group-type';
import { naturalSort } from '@linc-technologies/linced-common/utils/sorting';
import ReportingBaseMixin from 'linced-app/mixins/reporting-base';
import { serializeQueryParams } from 'linced-app/utils/url';

export default class IndexController extends Controller.extend(
  ReportingBaseMixin,
) {
  @service
  dates;

  @service
  router;

  @service
  session;

  @service
  store;

  @service
  students;

  // Overview data
  @tracked
  overviewCounts = null;

  @tracked overviewStudentReportCounts = null;

  @alias('students.currentCount')
  totalStudentCount;

  @tracked
  isLoadingOverview = false;

  @filterBy('visibleGroups', 'type', GroupType.HOMEROOM)
  classes;

  @filterBy('visibleGroups', 'type', GroupType.GROUP)
  groups;

  @filterBy('visibleGroups', 'type', GroupType.SHARED)
  shared;

  @filterBy('visibleGroups', 'type', GroupType.AUTO)
  autogroups;

  @computed(
    'parentController.{selectedGroupType,groupSearch}',
    'classes',
    'groups',
    'shared',
    'autogroups',
  )
  get filteredGroups() {
    let search = (this.parentController.get('groupSearch') || '')
      .toLowerCase()
      .trim();
    if (search) {
      let all = [
        ...this.classes,
        ...this.groups,
        ...this.shared,
        ...this.autogroups,
      ];
      return all
        .filter((group) => {
          let label = (group.get('label') || '').toLowerCase();
          let name = (group.get('name') || '').toLowerCase();
          return label.includes(search) || name.includes(search);
        })
        .sort(naturalSort('name'));
    }
    let type = this.parentController.get('selectedGroupType');
    return (this[type] || []).slice().sort(naturalSort('name'));
  }

  @computed(
    'overviewStudentReportCounts',
    'classes.@each.sortedCurrentLearners',
  )
  get classProgress() {
    let reportCounts = this.overviewStudentReportCounts;
    let classes = this.classes;
    if (!reportCounts || !classes) return [];

    return classes
      .map((group) => {
        let learners = group.get('sortedCurrentLearners');
        if (!learners) return null;
        let studentIds = learners
          .filter((l) => l.person)
          .map((l) => l.person.get('id'));
        if (!studentIds.length) return null;

        let counts = {
          inProgress: 0,
          inReview: 0,
          readyToPublish: 0,
          published: 0,
        };
        let totalReports = 0;
        studentIds.forEach((id) => {
          let sc = reportCounts[id];
          if (!sc) return;
          counts.published += sc.published;
          counts.readyToPublish += sc.readyToPublish;
          counts.inReview += sc.inReview;
          counts.inProgress += sc.inProgress;
          totalReports +=
            sc.published + sc.readyToPublish + sc.inReview + sc.inProgress;
        });

        return {
          label: group.get('label') || group.get('name'),
          totalReports,
          counts,
        };
      })
      .filter(Boolean)
      .sort(naturalSort('label'));
  }

  async loadOverview() {
    let parent = this.parentController;
    if (!parent.get('model.reportTemplates')) return;
    this.isLoadingOverview = true;

    let params = {
      schoolId: this.session.activeSchool,
      currentAcademicYear: this.dates.currentAcademicYear,
      currentMonth: new Date().getMonth() + 1,
    };

    try {
      let data = await parent.fetch.request(
        `${
          config.APP.apiHost
        }/reports/v4/school-student-report-counts?${serializeQueryParams(
          params,
        )}`,
      );

      let reportIdToStudentId = data.reportIdToStudentId || {};
      let uniquePublished = new Set();
      let uniqueReadyToPublish = new Set();
      let uniqueInProgress = new Set();
      let uniqueInReview = new Set();
      let reportCounts = {};

      let bump = (studentId, bucket) => {
        if (!reportCounts[studentId]) {
          reportCounts[studentId] = {
            published: 0,
            readyToPublish: 0,
            inReview: 0,
            inProgress: 0,
          };
        }
        reportCounts[studentId][bucket]++;
      };

      let bucketByReportIds = (reportIds, uniqueStudents, bucket) => {
        (reportIds || []).forEach((rid) => {
          let sid = reportIdToStudentId[rid];
          if (!sid) return;
          uniqueStudents.add(sid);
          bump(sid, bucket);
        });
      };

      (data.entries || []).forEach((entry) => {
        bucketByReportIds(entry.published, uniquePublished, 'published');
        bucketByReportIds(
          entry.readyToPublish,
          uniqueReadyToPublish,
          'readyToPublish',
        );
        bucketByReportIds(entry.inProgress, uniqueInProgress, 'inProgress');

        // inReview folds in readyToReview, proofreading, and any reports
        // awaiting a leadership comment. A READY_TO_REVIEW report awaiting
        // leadership is in both readyToReview and readyForLeadershipComment
        // upstream, so dedupe per-entry before counting.
        let reviewReportIds = new Set();
        (entry.readyToReview || []).forEach((rid) => reviewReportIds.add(rid));
        (entry.proofreading || []).forEach((rid) => reviewReportIds.add(rid));
        (entry.readyForLeadershipComment || []).forEach((rid) =>
          reviewReportIds.add(rid),
        );
        bucketByReportIds([...reviewReportIds], uniqueInReview, 'inReview');
      });

      this.overviewCounts = {
        published: uniquePublished.size,
        readyToPublish: uniqueReadyToPublish.size,
        inProgress: uniqueInProgress.size,
        inReview: uniqueInReview.size,
      };

      this.overviewStudentReportCounts = reportCounts;
    } catch (err) {
      this.overviewCounts = null;
      this.overviewStudentReportCounts = null;
    } finally {
      this.isLoadingOverview = false;
    }
  }

  @action
  selectGroupType(type) {
    this.parentController.set('selectedGroupType', type);
  }

  @action
  toggleGroupStudents(groupId) {
    let expandedGroups = this.parentController.get('expandedGroups');
    let pos = expandedGroups.indexOf(groupId);
    if (pos > -1) {
      expandedGroups.splice(pos, 1);
    } else {
      expandedGroups.push(groupId);
      this.reportingGroupBrowser.ensureGroupPersonsLoaded(groupId);
    }
  }

  @action
  selectGroup(group) {
    this.router.transitionTo('reporting.group', group.get('id'));
  }
}
